import { useEffect, useState } from 'react';

import { useDataModel } from '@/hooks/useDataModel';
import { useOrganization } from '@/hooks/useOrganization';
import { useUser } from '@/hooks/useUser';
import { Item } from '@/types/DataModel';

import { useDefaultItemQuery } from '../ItemEditor.queries';

/**
 * @returns the default item of the category.
 *
 * The hook is only enabled when we are creating a new item.
 *
 * Note that default item might be cached in the local storage, otherwise it's
 * fetched from the server.
 */
const useDefaultItem = (enabled: boolean, category?: string) => {
    const [fetchDefaultItem, setFetchDefaultItem] = useState(false);
    const [defaultItem, setDefaultItem] = useState<Item | undefined>();

    const { organization } = useOrganization();
    const { user } = useUser();
    const { dataModel } = useDataModel();

    const { data: fetchDefaultItemResponse, isLoading } = useDefaultItemQuery({
        variables: {
            category: category || '',
        },
        enabled: fetchDefaultItem,
    });

    useEffect(() => {
        // Don't keep a stale "fetch" flag around.
        if (!enabled) {
            setFetchDefaultItem(false);
            return;
        }

        // Category must exist to resolve a default item (cached or fetched).
        if (!category) {
            setFetchDefaultItem(false);
            setDefaultItem(undefined);
            return;
        }

        // Reset state for new category.
        setDefaultItem(undefined);
        setFetchDefaultItem(false);

        const cached = dataModel?.getDefaultItem(
            category,
            organization?.id,
            user?.id,
        );

        if (cached) {
            setDefaultItem(cached);
        } else {
            setFetchDefaultItem(true);
        }

        //eslint-disable-next-line
    }, [category, enabled, organization?.id, user?.id]);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        if (fetchDefaultItemResponse && category) {
            setDefaultItem(fetchDefaultItemResponse);
            dataModel?.setDefaultItem(
                category,
                fetchDefaultItemResponse,
                organization?.id,
                user?.id,
            );
            setFetchDefaultItem(false);
        }

        //eslint-disable-next-line
    }, [
        fetchDefaultItemResponse,
        enabled,
        category,
        organization?.id,
        user?.id,
    ]);

    const isDefaultItemLoading =
        enabled && !!category && fetchDefaultItem && isLoading;

    return {
        defaultItem,
        isLoading: enabled ? isDefaultItemLoading : false,
    };
};

export { useDefaultItem };
